<script setup lang="ts">
/**
 * PEyebrow — micro-libellé éditorial au-dessus d'un titre ou d'une section.
 *
 * Pattern signature Revelio : font-mono uppercase tracking-wider, taille 10px,
 * color ink-4 (kicker) ou ink-5 (mini-label section). Apparaît systématique-
 * ment au-dessus des h1/h2 et au-dessus de chaque donnée KPI.
 *
 * Exemples :
 *   <PEyebrow>Section 01 — Identité</PEyebrow>
 *   <PEyebrow tone="subtle">Score total</PEyebrow>
 *
 * Sémantique = juste un <span>. Le composant existe pour éviter que chaque
 * usage répète les 5 propriétés CSS et pour permettre une évolution
 * centralisée (ex: changer tracking).
 */
interface Props {
    /** muted = ink-4 (défaut, kicker au-dessus de h1). subtle = ink-5 (mini-label données). */
    tone?: 'muted' | 'subtle' | 'accent' | 'inverse'
    /** Tag HTML rendu (span défaut, p pour standalone, h2 pour section). */
    as?: 'span' | 'p' | 'h2' | 'h3' | 'div'
}

withDefaults(defineProps<Props>(), {
    tone: 'muted',
    as: 'span',
})
</script>

<template>
    <component :is="as" :class="['p-eyebrow', `p-eyebrow--${tone}`]">
        <slot />
    </component>
</template>

<style scoped>
.p-eyebrow {
    display: inline-block;
    font-family: var(--p-font-mono);
    font-size: var(--p-text-2xs);
    font-weight: var(--p-font-weight-medium);
    text-transform: uppercase;
    letter-spacing: var(--p-tracking-wider);
    margin: 0;
}

.p-eyebrow--muted   { color: var(--p-color-ink-4); }
.p-eyebrow--subtle  { color: var(--p-color-ink-5); }
.p-eyebrow--accent  { color: var(--p-color-olive); }
.p-eyebrow--inverse { color: var(--p-color-ink-5); }
</style>
